import React, { useState } from "react";
import { Image as ImageIcon } from "lucide-react";

import { cn } from "@/lib/utils";

function normalizeDriveImageUrl(input) {
  const src = String(input || "").trim();
  if (!src) return src;

  // Google Drive often blocks hotlinking. Convert common share formats to a more reliable direct image URL.
  // Supported:
  // - https://drive.google.com/file/d/<ID>/view?...
  // - https://drive.google.com/open?id=<ID>
  // - https://drive.google.com/uc?id=<ID>
  // Output uses googleusercontent (most reliable for <img>).
  const driveFileMatch = src.match(/drive\.google\.com\/file\/d\/([^/]+)\//i);
  const idParamMatch = src.match(/[?&]id=([^&]+)/i);
  const id = driveFileMatch?.[1] || idParamMatch?.[1];

  if (id && src.includes("drive.google.com")) {
    return `https://lh3.googleusercontent.com/d/${id}=w2000`;
  }

  return src;
}

export const SafeImage = ({
  src,
  alt,
  className,
  imgClassName,
  fallbackLabel,
  loading = "lazy",
  ...props
}) => {
  const [failed, setFailed] = useState(false);

  if (!src || failed) {
    return (
      <div
        aria-label={alt || fallbackLabel || "Image"}
        className={cn(
          "relative flex h-full w-full items-center justify-center overflow-hidden",
          "bg-[radial-gradient(circle_at_30%_20%,rgba(16,185,129,0.14),transparent_55%),radial-gradient(circle_at_70%_40%,rgba(245,158,11,0.10),transparent_55%)]",
          className
        )}
      >
        <div className="absolute inset-0 noise opacity-30" />
        <div className="relative flex flex-col items-center gap-2 text-muted-foreground">
          <ImageIcon className="h-5 w-5" />
          <div className="text-xs font-semibold">
            {fallbackLabel || alt || "Image unavailable"}
          </div>
        </div>
      </div>
    );
  }

  const normalizedSrc = normalizeDriveImageUrl(src);

  return (
    <img
      src={normalizedSrc}
      alt={alt}
      loading={loading}
      onError={() => setFailed(true)}
      className={cn("h-full w-full", imgClassName || "")}
      {...props}
    />
  );
};
